import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const SPORTS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' },
  { key: 'boxing_boxing', label: 'Boxing' },
  { key: 'golf', label: 'Golf' },
];

const inputCls = "w-full px-3 py-2 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#0f1219] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150";
const labelCls = "text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8] block mb-1";

function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-6 first:mt-0">
      <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
      <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>{children}</h3>
    </div>
  );
}

export default function SettingsPage() {
  const { user, loading: authLoading } = useAuth();

  const [profile, setProfile] = useState({
    display_name: '',
    odds_format: 'american',
    dark_mode: true,
    default_sport: 'americanfootball_nfl',
    favorite_sports: [],
    favorite_teams: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [teamInput, setTeamInput] = useState('');

  // Fetch profile on mount
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single()
      .then(({ data, error: err }) => {
        if (data) {
          setProfile({
            display_name: data.display_name || '',
            odds_format: data.odds_format || 'american',
            dark_mode: data.dark_mode ?? true,
            default_sport: data.default_sport || 'americanfootball_nfl',
            favorite_sports: data.favorite_sports || [],
            favorite_teams: data.favorite_teams || [],
          });
        }
        setLoading(false);
      });
  }, [user]);

  async function handleSave() {
    if (!user) return;
    setSaving(true);
    setError(null);
    setSuccess(false);

    const { error: err } = await supabase
      .from('user_profiles')
      .update({
        display_name: profile.display_name || null,
        odds_format: profile.odds_format,
        dark_mode: profile.dark_mode,
        default_sport: profile.default_sport,
        favorite_sports: profile.favorite_sports,
        favorite_teams: profile.favorite_teams,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (err) {
      setError(err.message);
    } else {
      setSuccess(true);
      // Apply preferences immediately
      localStorage.setItem('ai-sports-edge-dark-mode', String(profile.dark_mode));
      // Dispatch storage event so App.jsx picks up the dark mode change
      window.dispatchEvent(new Event('storage'));
      setTimeout(() => setSuccess(false), 3000);
    }
    setSaving(false);
  }

  function toggleFavoriteSport(key) {
    setProfile((p) => {
      const fav = p.favorite_sports.includes(key)
        ? p.favorite_sports.filter((s) => s !== key)
        : [...p.favorite_sports, key];
      return { ...p, favorite_sports: fav };
    });
  }

  function addTeam() {
    const name = teamInput.trim();
    if (!name || profile.favorite_teams.includes(name)) return;
    setProfile((p) => ({ ...p, favorite_teams: [...p.favorite_teams, name] }));
    setTeamInput('');
  }

  function removeTeam(name) {
    setProfile((p) => ({ ...p, favorite_teams: p.favorite_teams.filter((t) => t !== name) }));
  }

  // Auth gate
  if (authLoading) {
    return (
      <div className="px-5 py-6">
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="px-5 py-6">
        <div className="max-w-[400px] mx-auto mt-12">
          <div className="rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-6 py-8 text-center">
            <svg className="w-10 h-10 text-[#9CA3AF] dark:text-[#505a6e] mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h2 className="text-[18px] font-semibold text-[#111827] dark:text-[#e2e8f0] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>Sign in to manage settings</h2>
            <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Create an account to save your preferences, favorite sports, and teams.</p>
            <Link to="/auth" className="inline-block px-5 py-2.5 rounded-md bg-[#005BB3] hover:bg-[#004494] text-white text-[14px] font-semibold transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 py-6">
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Account</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Settings</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Manage your profile, preferences, and favorite sports.</p>

      {loading ? (
        <div className="max-w-[800px] space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      ) : (
        <div className="max-w-[800px]">
          {/* Profile */}
          <SectionHeader>Profile</SectionHeader>
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Display Name</label>
                <input type="text" value={profile.display_name} onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))}
                  placeholder="Your name" className={inputCls} style={{ fontFamily: 'var(--font-sans)' }} />
              </div>
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Email</label>
                <input type="text" value={user.email} readOnly
                  className={`${inputCls} opacity-60 cursor-not-allowed`} style={{ fontFamily: 'var(--font-sans)' }} />
              </div>
            </div>
          </div>

          {/* Preferences */}
          <SectionHeader>Preferences</SectionHeader>
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Odds Format</label>
                <select value={profile.odds_format} onChange={(e) => setProfile((p) => ({ ...p, odds_format: e.target.value }))}
                  className={inputCls} style={{ fontFamily: 'var(--font-sans)' }}>
                  <option value="american">American (+150 / -110)</option>
                  <option value="decimal">Decimal (2.50 / 1.91)</option>
                </select>
              </div>
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Dark Mode</label>
                <select value={profile.dark_mode ? 'on' : 'off'} onChange={(e) => setProfile((p) => ({ ...p, dark_mode: e.target.value === 'on' }))}
                  className={inputCls} style={{ fontFamily: 'var(--font-sans)' }}>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
              </div>
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Default Sport</label>
                <select value={profile.default_sport} onChange={(e) => setProfile((p) => ({ ...p, default_sport: e.target.value }))}
                  className={inputCls} style={{ fontFamily: 'var(--font-sans)' }}>
                  {SPORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Favorite Sports */}
          <SectionHeader>Favorite Sports</SectionHeader>
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-4 mb-4">
            <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>Select sports you follow for personalized content.</p>
            <div className="flex flex-wrap gap-2">
              {SPORTS.map((s) => {
                const active = profile.favorite_sports.includes(s.key);
                return (
                  <button key={s.key} onClick={() => toggleFavoriteSport(s.key)}
                    className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors duration-150 border ${
                      active
                        ? 'bg-[#005BB3] border-[#005BB3] text-white'
                        : 'border-[#E5E7EB] dark:border-[#1e2536] text-[#6B7280] dark:text-[#8892a8] hover:border-[#005BB3] hover:text-[#005BB3] dark:hover:text-[#3b82f6]'
                    }`}
                    style={{ fontFamily: 'var(--font-sans)' }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Favorite Teams */}
          <SectionHeader>Favorite Teams</SectionHeader>
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-4 mb-4">
            <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>Add teams you follow for prioritized alerts and predictions.</p>
            <div className="flex gap-2 mb-3">
              <input type="text" value={teamInput} onChange={(e) => setTeamInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTeam(); } }}
                placeholder="e.g. Kansas City Chiefs" className={`flex-1 ${inputCls}`} style={{ fontFamily: 'var(--font-sans)' }} />
              <button onClick={addTeam} disabled={!teamInput.trim()}
                className="px-3 py-2 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[13px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
                Add
              </button>
            </div>
            {profile.favorite_teams.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {profile.favorite_teams.map((team) => (
                  <span key={team} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0f1219] text-[13px] text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>
                    {team}
                    <button onClick={() => removeTeam(team)} className="text-[#9CA3AF] hover:text-[#DC2626] transition-colors duration-150">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-[#9CA3AF] dark:text-[#505a6e]" style={{ fontFamily: 'var(--font-sans)' }}>No teams added yet.</p>
            )}
          </div>

          {/* Save */}
          {error && (
            <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-3">
              <p className="text-[#DC2626] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>{error}</p>
            </div>
          )}
          {success && (
            <div className="rounded bg-[rgba(34,197,94,0.06)] dark:bg-[rgba(34,197,94,0.08)] border border-[#bbf7d0] dark:border-[#14532d] px-3 py-2 mb-3">
              <p className="text-[#029456] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>Settings saved successfully.</p>
            </div>
          )}
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-semibold transition-colors duration-150"
            style={{ fontFamily: 'var(--font-sans)' }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
}
